import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, ShieldCheck, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { CopyField } from "@/pages/admin/CopyField";

interface PaymentProvidersStatus {
  stripe: {
    configured: boolean;
    enabled: boolean;
    api_key_set: boolean;
    managed_by: string;
    note: string;
  };
  razorpay: {
    configured: boolean;
    managed_by: string;
    key_id: string;
    key_secret_set: boolean;
    webhook_secret_set: boolean;
    updated_at: string | null;
  };
}

/**
 * Stripe (platform-managed) + Razorpay (admin-managed) payment provider
 * configuration card. Stripe is fully managed by Emergent; Razorpay
 * accepts admin-provided API + webhook credentials.
 */
export const PaymentProvidersCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";
  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");

  const q = useQuery<PaymentProvidersStatus>({
    queryKey: ["payment-config"],
    queryFn: async () => (await api.get("/admin/payment-config")).data,
  });

  useEffect(() => {
    if (q.data?.razorpay.key_id) setKeyId(q.data.razorpay.key_id);
  }, [q.data?.razorpay.key_id]);

  const save = useMutation({
    mutationFn: async () =>
      (await api.put("/admin/payment-config/razorpay", {
        key_id: keyId.trim(),
        key_secret: keySecret.trim(),
        webhook_secret: webhookSecret.trim(),
      })).data,
    onSuccess: () => {
      toast.success("Razorpay credentials saved.");
      setKeySecret("");
      setWebhookSecret("");
      qc.invalidateQueries({ queryKey: ["payment-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const toggleStripe = useMutation({
    mutationFn: async (enabled: boolean) =>
      (await api.put("/admin/payment-config/stripe", { enabled })).data,
    onSuccess: (_d, enabled) => {
      toast.success(enabled ? "Stripe enabled." : "Stripe disabled.");
      qc.invalidateQueries({ queryKey: ["payment-config"] });
      qc.invalidateQueries({ queryKey: ["billing-plans"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const backendUrl = typeof window !== "undefined"
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || "");
  const razorpayWebhookUrl = `${backendUrl}/api/webhooks/razorpay`;

  const stripeOk = !!q.data?.stripe.configured;
  const rzpOk = !!q.data?.razorpay.configured;

  return (
    <div className="sg-card p-6 mt-6" data-testid="payment-providers-card">
      <div className="sg-label mb-2">Payment Providers</div>
      <div className="font-display text-lg font-semibold tracking-tight mb-1">
        Stripe (global · USD) + Razorpay (India · INR)
      </div>
      <p className="text-sm text-[color:var(--sg-muted)] mb-5 leading-relaxed max-w-3xl">
        SociBaba auto-routes each customer to the right payment provider based on their location.
        Stripe is managed by Emergent — no setup needed. Razorpay requires your own account and API keys.
      </p>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Stripe (managed) */}
        <div className="rounded-lg border border-[color:var(--sg-border)] p-5" data-testid="stripe-status-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-[#635BFF]" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <div className="font-display text-base font-semibold tracking-tight">Stripe</div>
              <div className="text-[11px] text-[color:var(--sg-muted)]">
                Global cards, wallets, USD-invoiced subscriptions
              </div>
            </div>
            {stripeOk ? (
              <span className="sg-chip bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="stripe-status-chip">
                <CheckCircle2 className="h-3 w-3" /> Live
              </span>
            ) : q.data?.stripe.api_key_set && q.data?.stripe.enabled === false ? (
              <span className="sg-chip bg-slate-100 text-slate-700 border-slate-200" data-testid="stripe-status-chip">
                <AlertCircle className="h-3 w-3" /> Disabled by admin
              </span>
            ) : (
              <span className="sg-chip bg-amber-50 text-amber-800 border-amber-200" data-testid="stripe-status-chip">
                <AlertCircle className="h-3 w-3" /> Waiting on platform
              </span>
            )}
          </div>
          <div className="text-xs text-[color:var(--sg-fg)] leading-relaxed mb-2">
            {q.data?.stripe.note || "Stripe is managed by Emergent using a platform-provided API key."}
          </div>
          <div className="text-[11px] text-[color:var(--sg-muted)] leading-relaxed mb-4">
            <b>What we serve:</b> customers whose currency resolves to USD/EUR/GBP/AUD/CAD.<br />
            <b>Webhooks:</b> auto-registered by the platform. Nothing to configure.
          </div>

          {/* Enable / Disable toggle */}
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-[color:var(--sg-border)]"
                data-testid="stripe-toggle-row">
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wider" htmlFor="stripe-enabled-toggle">
                Stripe checkout
              </Label>
              <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5">
                {q.data?.stripe.enabled
                  ? "Enabled — USD customers see Stripe checkout."
                  : "Disabled — USD checkouts will be rejected until re-enabled."}
                {!q.data?.stripe.api_key_set && (
                  <> Platform API key not yet provisioned.</>
                )}
              </div>
            </div>
            <Switch
              id="stripe-enabled-toggle"
              data-testid="stripe-enabled-toggle"
              checked={!!q.data?.stripe.enabled}
              disabled={readonly || toggleStripe.isPending || !q.data}
              onCheckedChange={(v) => toggleStripe.mutate(!!v)}
            />
          </div>
        </div>

        {/* Razorpay (admin-configured) */}
        <div className="rounded-lg border border-[color:var(--sg-border)] p-5" data-testid="razorpay-config-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-lg bg-blue-600/10 flex items-center justify-center">
              <KeyRound className="h-5 w-5 text-blue-700" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <div className="font-display text-base font-semibold tracking-tight">Razorpay</div>
              <div className="text-[11px] text-[color:var(--sg-muted)]">
                India cards, UPI, netbanking, INR-invoiced subscriptions
              </div>
            </div>
            {rzpOk ? (
              <span className="sg-chip bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle2 className="h-3 w-3" /> Live
              </span>
            ) : (
              <span className="sg-chip bg-amber-50 text-amber-800 border-amber-200">
                <AlertCircle className="h-3 w-3" /> Setup pending
              </span>
            )}
          </div>

          <div className="space-y-2 text-[11px] text-[color:var(--sg-muted)] mb-4 leading-relaxed">
            <div className="font-semibold text-[10px] uppercase tracking-wider text-[color:var(--sg-fg)]">
              5-step guide
            </div>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Sign up at <a className="text-[color:var(--sg-primary)] hover:underline" href="https://dashboard.razorpay.com/signup" target="_blank" rel="noreferrer">dashboard.razorpay.com</a>. Complete KYC to activate live mode.</li>
              <li>Dashboard → <b>Account &amp; Settings</b> → <b>API Keys</b> → <b>Generate Test Key</b> (or Live Key after KYC). Copy the <b>Key ID</b> + <b>Key Secret</b>.</li>
              <li>Dashboard → <b>Settings</b> → <b>Webhooks</b> → <b>Add New Webhook</b>. Paste this URL:</li>
            </ol>
            <div className="my-1"><CopyField value={razorpayWebhookUrl} testId="razorpay-webhook-copy" /></div>
            <ol className="list-decimal ml-4 space-y-1" start={4}>
              <li>Subscribe to events: <span className="font-mono text-[10px]">subscription.activated, subscription.charged, subscription.cancelled, subscription.completed, payment.failed, refund.processed</span>. Copy the <b>Webhook Secret</b>.</li>
              <li>Paste all three values below and save.</li>
            </ol>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Razorpay Key ID</Label>
              <Input
                data-testid="razorpay-key-id"
                placeholder="rzp_test_XXXXXXXXXXXX or rzp_live_XXXXXXXXXXXX"
                value={keyId}
                disabled={readonly}
                onChange={(e) => setKeyId(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Razorpay Key Secret</Label>
              <Input
                data-testid="razorpay-key-secret"
                type="password"
                placeholder={q.data?.razorpay.key_secret_set ? "•••••••• (leave blank to keep)" : "secret"}
                value={keySecret}
                disabled={readonly}
                onChange={(e) => setKeySecret(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Webhook Secret</Label>
              <Input
                data-testid="razorpay-webhook-secret"
                type="password"
                placeholder={q.data?.razorpay.webhook_secret_set ? "•••••••• (leave blank to keep)" : "webhook secret"}
                value={webhookSecret}
                disabled={readonly}
                onChange={(e) => setWebhookSecret(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="text-[11px] text-[color:var(--sg-muted)]">
                {q.data?.razorpay.updated_at
                  ? <>Last updated {new Date(q.data.razorpay.updated_at).toLocaleString()}</>
                  : <>No credentials saved yet.</>}
              </div>
              {!readonly && (
                <Button
                  data-testid="razorpay-save-btn"
                  size="sm"
                  disabled={save.isPending || (!keyId.trim() && !keySecret.trim() && !webhookSecret.trim())}
                  onClick={() => save.mutate()}
                  className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white h-8"
                >
                  {save.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Save Razorpay
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
